<template>
    <div class="stores-page">
        <!-- Header -->
        <div class="page-header">
            <div>
                <h1>{{ $t('menu.storeLocator.page.header') }}</h1>
                <p class="page-subtitle">{{ $t('menu.overview.page.storesWorldwide', { count: user.stores.length }) }}</p>
            </div>
            <button class="appButton" @click="addStore()">
                <i class="bx bx-plus" style="margin-right: 0.25rem;"></i>
                {{ $t('menu.storeLocator.page.storeLocatorOverview.add') }}
            </button>
        </div>

        <!-- Search -->
        <div class="search-bar">
            <div class="search-input-wrap">
                <i class="bx bx-search search-icon"></i>
                <input @input="filterStores()" v-model="user.store.filters.searchString"
                       type="text" class="tap-input search-field"
                       :placeholder="$t('menu.storeLocator.page.storeLocatorOverview.search') + '...'" />
            </div>
        </div>

        <!-- Search Results -->
        <div v-if="stores.length > 0" class="table-section">
            <div class="table-header">
                <h2>{{ $t('menu.storeLocator.page.searchResults') }}</h2>
                <span class="tap-badge tap-badge-accent">{{ stores.length }}</span>
            </div>
            <div class="tap-card table-wrap">
                <table class="tap-table">
                    <thead>
                        <tr>
                            <th @click="sortSearchBy('country')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.country') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortSearchBy('city')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.city') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortSearchBy('name')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.name') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortSearchBy('address_line_1')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.address1') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortSearchBy('postal_code')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.postal_code') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortSearchBy('state_province')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.state') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="s2 in stores" :key="s2.p_id">
                            <td>{{ s2.country }}</td>
                            <td>{{ s2.city }}</td>
                            <td class="cell-name">{{ s2.name }}</td>
                            <td>{{ s2.address_line_1 }}</td>
                            <td><span class="cell-mono">{{ s2.postal_code }}</span></td>
                            <td>{{ s2.state_province }}</td>
                            <td class="cell-action">
                                <button class="row-action-btn" @click="editStore(s2)">
                                    <i class="bx bx-edit-alt"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- New Stores -->
        <div v-if="user.storeStatusNew && user.storeStatusNew.length > 0" class="table-section">
            <div class="table-header">
                <h2>{{ $t('menu.storeLocator.page.newStores') }}</h2>
                <span class="tap-badge tap-badge-amber">{{ user.storeStatusNew.length }}</span>
            </div>
            <div class="tap-card table-wrap">
                <table class="tap-table">
                    <thead>
                        <tr>
                            <th @click="sortNewBy('country')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.country') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortNewBy('city')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.city') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortNewBy('name')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.name') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortNewBy('address_line_1')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.address1') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortNewBy('postal_code')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.postal_code') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortNewBy('state_province')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.state') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="s2 in user.storeStatusNew" :key="s2.p_id">
                            <td>{{ s2.country }}</td>
                            <td>{{ s2.city }}</td>
                            <td class="cell-name">{{ s2.name }}</td>
                            <td>{{ s2.address_line_1 }}</td>
                            <td><span class="cell-mono">{{ s2.postal_code }}</span></td>
                            <td>{{ s2.state_province }}</td>
                            <td class="cell-action">
                                <button class="row-action-btn" @click="editStore(s2)">
                                    <i class="bx bx-edit-alt"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- All Stores -->
        <div class="table-section">
            <div class="table-header">
                <h2>{{ $t('menu.storeLocator.page.existingStores') }}</h2>
                <span class="tap-badge tap-badge-accent">{{ user.stores.length }}</span>
            </div>
            <div class="tap-card table-wrap">
                <table class="tap-table">
                    <thead>
                        <tr>
                            <th @click="sortBy('country')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.country') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortBy('city')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.city') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortBy('name')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.name') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortBy('address_line_1')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.address1') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortBy('postal_code')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.postal_code') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th @click="sortBy('state_province')">
                                {{ $t('menu.storeLocator.page.storeLocatorOverview.state') }}
                                <i class="bx bx-sort-alt-2 sort-icon"></i>
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="store in user.stores" :key="store.p_id">
                            <td>{{ store.country }}</td>
                            <td>{{ store.city }}</td>
                            <td class="cell-name">{{ store.name }}</td>
                            <td>{{ store.address_line_1 }}</td>
                            <td><span class="cell-mono">{{ store.postal_code }}</span></td>
                            <td>{{ store.state_province }}</td>
                            <td class="cell-action">
                                <button class="row-action-btn" @click="editStore(store)">
                                    <i class="bx bx-edit-alt"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { useSettings } from '@/store/user'
import { storeToRefs } from 'pinia'
import mixins from '@/components/mixins';

export default {
    mixins: [mixins],
    setup() {
        const user = useSettings();
        const { users } = storeToRefs(user)

        user.getStores();

        return { user }
    },
    data() {
        return {
            stores: []
        }
    },
    methods: {
        async filterStores() {
            if(this.user.store.filters.searchString === '') {
                this.stores = [];
                return;
            }

            const searchFilter = this.user.store.filters.searchString.toLowerCase();

            this.stores = this.user.stores.filter(store => {
                const matchesSearch = store.name.toLowerCase().includes(searchFilter) ||
                                    store.city.toLowerCase().includes(searchFilter) ||
                                    store.state_province.toLowerCase().includes(searchFilter) ||
                                    store.country.toLowerCase().includes(searchFilter) ||
                                    store.postal_code.toLowerCase().includes(searchFilter);

                return matchesSearch;
            });
        },
        async addStore() {
            this.$router.push({ name: 'add-store-locator' });
        },
        async editStore(store) {
            this.user.editStore = store;
            this.$router.push({ name: 'edit-store-locator', params: { p_id: store.p_id } });
        },
        async sortBy(column) {
            this.user.store.sorting.column = column;

            this.user.stores.sort((a, b) => {
                if (a[column] < b[column]) return -1;
                if (a[column] > b[column]) return 1;
                return 0;
            });
        },
        async sortNewBy(column) {
            this.user.storeStatusNew.sort((a, b) => {
                if (a[column] < b[column]) return -1;
                if (a[column] > b[column]) return 1;
                return 0;
            });
        },
        async sortSearchBy(column) {
            this.stores.sort((a, b) => {
                if (a[column] < b[column]) return -1;
                if (a[column] > b[column]) return 1;
                return 0;
            });
        }
    }
}
</script>

<style scoped>
.stores-page {
}

.page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 1.5rem;
}
.page-subtitle {
    color: #9aa0a8;
    font-size: 0.875rem;
    margin-top: 0.125rem;
}

.search-bar {
    margin-bottom: 2rem;
}
.search-input-wrap {
    position: relative;
    max-width: 400px;
}
.search-icon {
    position: absolute;
    left: 0.875rem;
    top: 50%;
    transform: translateY(-50%);
    color: #5c6370;
    font-size: 1.125rem;
}
.search-field {
    padding-left: 2.5rem !important;
}

.table-section {
    margin-bottom: 2rem;
    animation: fadeIn 0.4s ease-out both;
}
.table-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}
.table-wrap {
    overflow-x: auto;
    border-radius: 12px;
}

.sort-icon {
    font-size: 0.75rem;
    opacity: 0.5;
    margin-left: 0.25rem;
    vertical-align: middle;
}

.cell-name {
    color: #e8eaed;
    font-weight: 500;
}
.cell-mono {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.8125rem;
    color: #9aa0a8;
}
.cell-action {
    text-align: right;
    padding-right: 1rem;
}
.row-action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(255, 255, 255, 0.03);
    color: #9aa0a8;
    font-size: 1rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}
.row-action-btn:hover {
    background: rgba(117, 35, 35, 0.1);
    border-color: rgba(117, 35, 35, 0.2);
    color: #a63c3c;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
</style>
